import { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import {
    fetchPhotosAdminService,
    uploadPhotoService,
    updatePhotoService,
    deletePhotoService,
    reorderPhotosService,
} from '../../services/photosService';
import { STORAGE } from '../../utils/headers';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import './gallery.css';

/**
 * Gestión de la galería de la portada.
 *
 * Permite subir, ocultar, reordenar y borrar las imágenes que ve el visitante,
 * sin necesidad de un despliegue.
 */
export default function Gallery() {
    useDocumentMeta({ title: 'Galería', noIndex: true });

    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const cargar = () => {
        setLoading(true);
        fetchPhotosAdminService()
            .then((data) => setPhotos(data || []))
            .catch((error) => setStatus({ type: 'error', message: error.message }))
            .finally(() => setLoading(false));
    };

    useEffect(cargar, []);

    const subir = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        setUploading(true);
        setStatus({ type: '', message: '' });

        const creadas = [];
        for (const file of files) {
            try {
                creadas.push(await uploadPhotoService(file));
            } catch (error) {
                setStatus({ type: 'error', message: `${file.name}: ${error.message}` });
            }
        }

        if (creadas.length) {
            setPhotos((prev) => [...prev, ...creadas]);
            setStatus({
                type: 'success',
                message: `${creadas.length} ${creadas.length === 1 ? 'imagen publicada' : 'imágenes publicadas'} en la galería.`,
            });
        }
        setUploading(false);
    };

    const alternarVisibilidad = async (photo) => {
        try {
            const actualizada = await updatePhotoService(photo.id, { state: !photo.state });
            setPhotos((prev) => prev.map((p) => (p.id === photo.id ? actualizada : p)));
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        }
    };

    const guardarAlt = async (photo, alt) => {
        if (alt === (photo.alt || '')) return;
        try {
            const actualizada = await updatePhotoService(photo.id, { alt });
            setPhotos((prev) => prev.map((p) => (p.id === photo.id ? actualizada : p)));
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        }
    };

    const eliminar = async (photo) => {
        if (!window.confirm('¿Eliminar esta imagen de la galería? No se puede deshacer.')) return;
        try {
            await deletePhotoService(photo.id);
            setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        }
    };

    const mover = async (indice, direccion) => {
        const destino = indice + direccion;
        if (destino < 0 || destino >= photos.length) return;

        const reordenadas = [...photos];
        [reordenadas[indice], reordenadas[destino]] = [reordenadas[destino], reordenadas[indice]];
        setPhotos(reordenadas);

        try {
            await reorderPhotosService(reordenadas.map((p) => p.id));
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            cargar();
        }
    };

    const publicadas = photos.filter((p) => p.state).length;

    return (
        <div className="admin-container">
            <Container fluid>
                <div className="admin-header">
                    <div>
                        <h2 className="admin-title">Galería de la portada</h2>
                        <p className="gallery-summary mb-0">
                            {publicadas} de {photos.length} imágenes visibles para los visitantes
                        </p>
                    </div>
                    <label className={`btn-create-model ${uploading ? 'disabled' : ''}`}>
                        <i className="fa-solid fa-cloud-arrow-up"></i>
                        <span>{uploading ? 'Subiendo...' : 'Subir imágenes'}</span>
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            hidden
                            disabled={uploading}
                            onChange={subir}
                        />
                    </label>
                </div>

                {status.message && (
                    <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 small`}>
                        {status.message}
                    </div>
                )}

                {loading ? (
                    <p className="text-muted text-center py-5">Cargando galería...</p>
                ) : photos.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fa-solid fa-images mb-3" style={{ fontSize: '2rem' }}></i>
                        <p>
                            La galería está vacía. Mientras no publiques ninguna imagen, la
                            portada muestra las que vienen incluidas en la aplicación.
                        </p>
                    </div>
                ) : (
                    <div className="gallery-grid">
                        {photos.map((photo, indice) => (
                            <figure className={`gallery-item ${photo.state ? '' : 'is-hidden'}`} key={photo.id}>
                                <div className="gallery-thumb">
                                    <img
                                        src={`${STORAGE}/${photo.url}`}
                                        alt={photo.alt || ''}
                                        loading="lazy"
                                        onError={(e) => {
                                            e.target.closest('.gallery-thumb').classList.add('is-broken');
                                        }}
                                    />
                                    <span className="gallery-position">{indice + 1}</span>
                                    {!photo.state && <span className="gallery-hidden-tag">Oculta</span>}
                                </div>

                                <figcaption>
                                    <input
                                        className="gallery-alt"
                                        defaultValue={photo.alt || ''}
                                        placeholder="Texto alternativo (SEO)"
                                        maxLength={160}
                                        onBlur={(e) => guardarAlt(photo, e.target.value)}
                                    />
                                    <div className="gallery-actions">
                                        <button
                                            type="button"
                                            title="Mover antes"
                                            disabled={indice === 0}
                                            onClick={() => mover(indice, -1)}
                                        >
                                            <i className="fa-solid fa-arrow-left"></i>
                                        </button>
                                        <button
                                            type="button"
                                            title="Mover después"
                                            disabled={indice === photos.length - 1}
                                            onClick={() => mover(indice, 1)}
                                        >
                                            <i className="fa-solid fa-arrow-right"></i>
                                        </button>
                                        <button
                                            type="button"
                                            title={photo.state ? 'Ocultar de la portada' : 'Publicar en la portada'}
                                            onClick={() => alternarVisibilidad(photo)}
                                        >
                                            <i className={`fa-solid ${photo.state ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                        </button>
                                        <button
                                            type="button"
                                            title="Eliminar"
                                            className="danger"
                                            onClick={() => eliminar(photo)}
                                        >
                                            <i className="fa-solid fa-trash-can"></i>
                                        </button>
                                    </div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                )}
            </Container>
        </div>
    );
}
